import { AfterViewInit, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { ChartComponent } from '@next/core-lib/chart';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import * as Highcharts from 'highcharts';

import {
  FeedAdvice,
  ParityRegistration,
  ParityType,
  SowFeedAdviceLine,
} from '../../../models/feed-advice.model';

@Component({
  selector: 'app-sows-feed-advice-graph',
  templateUrl: './sows-feed-advice-graph.component.html',
  styles: ['.chart-container { touch-action: pan-y pinch-zoom; }'],
  imports: [
    MatRadioGroup,
    ReactiveFormsModule,
    FormsModule,
    MatRadioButton,
    ChartComponent,
    TranslatePipe,
  ],
})
export class SowsFeedAdviceGraphComponent implements OnInit, AfterViewInit {
  @Input() advice: FeedAdvice;
  @Input() lines: SowFeedAdviceLine[];
  @Input() parityCategory: ParityType;
  yAxis = 'feedintake';
  chartOptions: Highcharts.Options;
  chartSeries: Highcharts.SeriesOptionsType[];
  @ViewChild(ChartComponent) chartComponent: ChartComponent;

  constructor(@Inject(LocaleService) private readonly localeService: LocaleService) {}

  ngOnInit() {
    this.chartOptions = this.getInitialOptions();
    this.createChart();
  }

  ngAfterViewInit(): void {
    this.changeSetting();
  }

  private getChartSeriesName(parity: number, index: number): string {
    let parityNumber: string;

    if (this.advice && this.advice.useParitySystem === false) {
      return index === 0
        ? this.advice.parityAlternative2
        : index === 1
          ? this.advice.parityAlternative3
          : index === 2
            ? this.advice.parityAlternative4
            : index === 3
              ? this.advice.parityAlternative5
              : index === 4
                ? this.advice.parityAlternative6
                : '';
    } else {
      switch (this.parityCategory) {
        case ParityType.ONETOTWO:
          parityNumber = parity === 2 ? `>= ${parity}` : `${parity}`;
          break;
        case ParityType.TWOTOFOUR:
          if (parity === 1) {
            parityNumber = '1';
          } else if (parity === 2) {
            parityNumber = '2-4';
          } else if (parity === 3) {
            parityNumber = '>= 5';
          }
          break;
        case ParityType.ONETOFIVE:
          parityNumber = parity === 5 ? `>= ${parity}` : `${parity}`;
          break;
      }
      return `${this.localeService.translate('parity')} ${parityNumber}`;
    }
  }

  createChart(): void {
    this.chartSeries = [];
    const parityRegistrations = this.aggregateParityRegistrations(this.lines);
    parityRegistrations
      .filter((pr, index, self) => index === self.findIndex((p) => p.parity === pr.parity))
      .forEach((pr, index, __) => {
        this.chartSeries.push({
          name: this.getChartSeriesName(pr.parity, index),
          type: 'line',
          data: this.getData(
            this.yAxis,
            parityRegistrations.filter((pr) => pr.parity === index + 1),
          ),
        });
      });
    this.setCategories();
  }

  changeSetting() {
    this.chartComponent.options = <Highcharts.Options>{
      ...this.chartOptions,
      yAxis: [
        {
          title: this.localeService.translate(`feed-advice-graph.${this.yAxis}`),
        },
      ],
    };
    this.getData(null, null);

    // redrawing the graph
    for (const _ of this.chartSeries) {
      this.chartComponent.chart.removeSeries(0);
    }

    const parityRegistrations = this.aggregateParityRegistrations(this.lines);
    parityRegistrations
      .filter((pr, index, self) => index === self.findIndex((p) => p.parity === pr.parity))
      .forEach((pr, index, __) => {
        this.chartComponent.chart.addSeries(
          {
            name: this.getChartSeriesName(pr.parity, index),
            type: 'line',
            data: this.getData(
              this.yAxis,
              parityRegistrations.filter((pr) => pr.parity === index + 1),
            ),
          },
          true,
          false,
        );
      });

    this.setCategories();
  }

  private setCategories() {
    if (
      this.chartComponent &&
      this.chartComponent.chartRef &&
      this.chartComponent.chartRef.xAxis &&
      this.chartComponent.chartRef.xAxis.length > 0
    ) {
      const data = ([] as string[]).concat(
        ...this.lines.map((line) => {
          const data: string[] = [];
          for (let index = 0; index <= line.dayTo - line.dayFrom; index++) {
            data.push((line.dayFrom + index).toString());
          }
          return data;
        }),
      );

      this.chartComponent.chartRef.xAxis[0].setCategories(data);
    }
  }

  private getInitialOptions(): Highcharts.Options {
    return {
      chart: {
        height: 450,
        width: 720,
        zooming: {
          mouseWheel: false,
        },
      },
      xAxis: {
        title: {
          enabled: true,
          text: this.localeService.translate('day'),
        },
      },
      yAxis: [
        {
          title: this.localeService.translate(`feed-advice-graph.${this.yAxis}`),
        },
      ],
      tooltip: {
        shared: true,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          step: 'left',
        },
        line: {
          animation: false,
        },
      },
    } as Highcharts.Options;
  }

  private getData(dataElement: string, parityRegistrations: ParityRegistration[]) {
    if (!this.lines) {
      return [];
    }

    return ([] as number[]).concat(
      ...this.lines.map((line, index) => {
        let val;
        const data: number[] = [];
        switch (dataElement) {
          case 'energy':
            val = Math.round((parityRegistrations[index].ewKg + Number.EPSILON) * 100) / 100;
            break;
          case 'lysine':
            val = Math.round((parityRegistrations[index].lysKg + Number.EPSILON) * 100) / 100;
            break;
          case 'feedintake':
            val = Math.round((parityRegistrations[index].feedKg + Number.EPSILON) * 100) / 100;
            break;
        }

        for (let index = 0; index <= line.dayTo - line.dayFrom; index++) {
          data.push(val);
        }
        return data;
      }),
    );
  }

  private aggregateParityRegistrations(lines: SowFeedAdviceLine[]) {
    const parityRegistrations: ParityRegistration[] = [];

    lines.forEach((line) => {
      line.parityRegistrations.forEach((linePr) => {
        parityRegistrations.push(linePr);
      });
    });

    return parityRegistrations;
  }
}
